import React, { useRef } from 'react'

import { useTranslation } from '@application/Interfaces/Hooks/useTranslation'
import { InternationalRelocationsIcon, OceanLCLIcon, ParcelsCouriersIcon } from '@application/Interfaces/Icons'
import { POPEYE_QUOTE_URL } from '@core/Constants/RouteConstants'
import { Link } from '@src/Application/Interfaces/Atoms/Link'
import { HeroProfileComponentType } from '@src/Application/Types/ComponentTypes'
import { handleDeferredRedirect } from '@src/Application/Utils/BrowserUtils'
import { getCompletePath } from '@src/Application/Utils/StringUtility'

import {
    ArrowDirectionIcon,
    IconWrapper,
    Option,
    OptionDescription,
    OptionTitle,
    ProfileContainer,
    ProfileContent,
    ProfileHeaderSection,
    ProfileTitle,
    QuestionLabel,
    StudentOption,
    TextWrapper,
} from './HeroProfileSection.styled'

type HeroProfileSectionProps = Pick<HeroProfileComponentType, 'profileTitle' | 'profileQuestionLabel'>

export const HeroProfileSection = (props: HeroProfileSectionProps): JSX.Element => {
    const { profileTitle, profileQuestionLabel } = props

    const { t } = useTranslation()
    const clickedRef = useRef(false)

    const options = [
        {
            title: t('profileOption1Title'),
            description: t('profileOption1Description'),
            queryParam: 'commercial',
            icon: OceanLCLIcon,
            animation: getCompletePath('/resources/images/quoteFormAnimations/ocean-lcl.gif'),
            altText: 'Ocean LCL icon',
        },
        {
            title: t('profileOption2Title'),
            description: t('profileOption2Description'),
            queryParam: 'relocation',
            icon: InternationalRelocationsIcon,
            animation: getCompletePath('/resources/images/quoteFormAnimations/international-relocations.gif'),
            altText: 'International Relocations icon',
        },
        {
            title: t('profileOption3Title'),
            description: t('profileOption3Description'),
            queryParam: 'courier',
            icon: ParcelsCouriersIcon,
            animation: getCompletePath('/resources/images/quoteFormAnimations/parcels-couriers.gif'),
            altText: 'Parcels and Couriers icon',
        },
    ]

    return (
        <ProfileContainer>
            <ProfileHeaderSection>
                <ProfileTitle>{profileTitle || t('defaultProfileTitle')}</ProfileTitle>
            </ProfileHeaderSection>
            <ProfileContent>
                <QuestionLabel>{profileQuestionLabel || t('defaultProfileQuestionLabel')}</QuestionLabel>
                {options.map((option) => {
                    const IconComponent = option.icon

                    return (
                        <Option
                            key={option.title}
                            href={`${POPEYE_QUOTE_URL}?quoteCategory=${option.queryParam}`}
                            onClick={handleDeferredRedirect(
                                `${POPEYE_QUOTE_URL}?quoteCategory=${option.queryParam}`,
                                clickedRef
                            )}
                        >
                            <IconWrapper>
                                <IconComponent />
                                <img src={option.animation} width={50} height={50} alt={option.altText} />
                            </IconWrapper>
                            <TextWrapper>
                                <OptionTitle>{option.title}</OptionTitle>
                                <OptionDescription>{option.description}</OptionDescription>
                            </TextWrapper>
                            <ArrowDirectionIcon colorCode="p500" />
                        </Option>
                    )
                })}
                <StudentOption>
                    <Link
                        to={`${POPEYE_QUOTE_URL}?quoteCategory=student`}
                        onClick={handleDeferredRedirect(`${POPEYE_QUOTE_URL}?quoteCategory=student`, clickedRef)}
                        underline
                    >
                        {t('profileOption4Title')}
                    </Link>
                </StudentOption>
            </ProfileContent>
        </ProfileContainer>
    )
}
